import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { EvaluationTreeActions } from './evaluation-tree.actions';
import { EvaluationTreeService } from '../../service/evaluationTree.service';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';


@Injectable()
export class EvaluationTreeEffects {

  loadEvaluationTree$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.loadEvaluationTree),
        mergeMap(({ campaignId, legalEntityId, parameters, fields, au, completed }) =>
          rs.getEvaluationTree(campaignId, legalEntityId, parameters, au, completed).pipe(
            map((evaluation) => {
              return EvaluationTreeActions.loadEvaluationTreeSuccess({
                evaluation,
                fields
              });
            }),
            catchError((error) => of(EvaluationTreeActions.loadEvaluationTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllEvaluationTree$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(
        ofType(EvaluationTreeActions.loadAllEvaluationTree),
        mergeMap(({ campaignId, legalEntityId, fields }) =>
          rs.getAllEvaluationTree(campaignId, legalEntityId).pipe(
            mergeMap((evaluation) => {
              return [EvaluationTreeActions.loadAllEvaluationTreeSuccess({
                evaluation,
                fields
              })];
            }),
            catchError((error) =>
              of(EvaluationTreeActions.loadAllEvaluationTreeFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  loadEvaluationOverall$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.loadEvaluationsOverall),
        mergeMap(({ campaignId, legalEntityId, parameters }) =>
          rs.getEvaluationOverall(campaignId, legalEntityId, parameters).pipe(
            map((riskMap) => {
              return EvaluationTreeActions.loadEvaluationsOverallSuccess({
                riskMap
              });
            }),
            catchError((error) => of(EvaluationTreeActions.loadEvaluationsOverallFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateEvaluation$ = createEffect(
    (
      rs: EvaluationTreeService = inject(EvaluationTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(EvaluationTreeActions.updateEvaluations),
        mergeMap(({ icsId, impactId, likelihoodId, riskId, riskMapId, parameterName, cptIds, campaignId, legalEntityId, filter }) =>
          rs.updateEvaluation(riskMapId, String(riskId), impactId, likelihoodId, icsId, filter).pipe(
            mergeMap((riskMap) => [
              EvaluationTreeActions.updateEvaluationsSuccess({
                riskMap,
                parameterName,
                cptIds
              }),
              EvaluationTreeActions.loadRiskOverall({ campaignId, legalEntityId })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Evaluation Error',
                detail: graphQlErrorMessage(error, 'An error occurred while updating evaluation'),
                life: 10000
              });
              return of(EvaluationTreeActions.updateEvaluationsFailure({ error, cptIds, parameterName }));
            }),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );

  updateEvaluationMultiple$ = createEffect(
    (
      rs: EvaluationTreeService = inject(EvaluationTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(EvaluationTreeActions.updateEvaluationsMultiple),
        mergeMap(({ updateEvaluationRequest, au, campaignId, completed, legalEntityId, parameters, fields }) =>
          rs.updateEvaluationMultiple(updateEvaluationRequest).pipe(
            tap(() => {
              // messageService.add({ severity: 'warn', summary: 'Massive Edit', detail: 'The changes will be applied in a few minutes' });
            }),
            mergeMap((riskMap) => [
              EvaluationTreeActions.loadEvaluationTree({
                au,
                campaignId,
                completed,
                legalEntityId,
                parameters,
                fields
              })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Massive Edit Error',
                detail: graphQlErrorMessage(error, 'An error occurred while updating evaluation'),
                life: 10000
              });
              return of(EvaluationTreeActions.updateEvaluationsMultipleFailure({ error }));
            }),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );


  getRiskOverall$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.loadRiskOverall),
        mergeMap(({ campaignId, legalEntityId }) =>
          rs.getRiskOverall(campaignId, legalEntityId).pipe(
            map((evaluation) => {
              return EvaluationTreeActions.loadRiskOverallSuccess({
                evaluation
              });
            }),
            catchError((error) => of(EvaluationTreeActions.loadRiskOverallFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  downloadQualitativeInputTree$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.exportEvaluationTree),
        mergeMap(({ campaignId, legalEntityId, parameters, timeZone }) =>
          rs.exportEvaluationTree(campaignId, legalEntityId, parameters, timeZone
          ).pipe(
            map((riskMap) => {
              this.downloadFile(riskMap!.data![0]!.content, riskMap!.data![0]!.filename)
              return EvaluationTreeActions.exportEvaluationTreeSuccess({
                riskMap
              });
            }),
            catchError((error) => of(EvaluationTreeActions.exportEvaluationTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  updateTreeNote$ = createEffect(
    (
      rs: EvaluationTreeService = inject(EvaluationTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(EvaluationTreeActions.updateTreeNote),
        mergeMap(({ campaignId, legalEntityId, processCode, cptIds, parameterName, note, filter }) =>
          rs.updateTreeNote(campaignId, legalEntityId, processCode, note, filter).pipe(
            mergeMap((riskMap) => [
              EvaluationTreeActions.updateTreeNoteSuccess({
                riskMap,
                cptIds,
                parameterName,
                note,
                filter
              }),
              // 👉 Azione aggiuntiva che viene eseguita dopo il successo
              EvaluationTreeActions.loadAllEvaluationTree({
                campaignId,
                legalEntityId
              })
              // eventualmente: ricarica dati, aggiorna UI, ecc.
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Note Error',
                detail: graphQlErrorMessage(error, 'Unable to update the note'),
                life: 10000
              });
              return of(
                EvaluationTreeActions.updateTreeNoteFailure({
                  error,
                  cptIds,
                  parameterName
                })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  requestMassiveEdit$ = createEffect(
    (
      rs: EvaluationTreeService = inject(EvaluationTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.requestMassiveEdit),
        mergeMap(({ riskMapTreeValueIds, icsId, impactId, likelihoodId, campaignId, legalEntityId }) =>
          rs.requestMassiveEdit(riskMapTreeValueIds, icsId, impactId, likelihoodId).pipe(
            map((request) => {
              return EvaluationTreeActions.requestMassiveEditSuccess({
                request
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Massive Edit Error',
                detail: graphQlErrorMessage(error, 'Unable to start the massive edit'),
                life: 10000
              });
              return of(EvaluationTreeActions.requestMassiveEditFailure({ error }));
            }),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );

  isLegalEntityEvaluationMassiveEditInProgress$ = createEffect(
    (rs: EvaluationTreeService = inject(EvaluationTreeService)) => {
      return this.actions$.pipe(

        ofType(EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress),
        mergeMap(({ campaignId, legalEntityId }) =>
          rs.isLegalEntityEvaluationMassiveEditInProgress(campaignId, legalEntityId).pipe(
            map((isLegalEntityEvaluationMassiveEditInProgress) => {
              return EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgressSuccess({
                isLegalEntityEvaluationMassiveEditInProgress
              });
            }),
            catchError((error) => of(EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgressFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
